import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ARTISTS } from "@/data/artists";
import { inferGenres, allGenres, artistMatchesFilters } from "@/lib/genres";

const AUDIO_BASE = "/samples/musical-collection";
const LS_KEY = "resilience:collection:v1";

/**
 * The Resilience Studios Musical Collection controller.
 * Founder plain-ink 2026-07-02+: on-site channel with by-genre + by-band
 * toggles. Persistent bottom-bar player with continuous playback across
 * enabled bands. State persisted to localStorage.
 *
 * Renders a fixed bottom bar. Owns the shared <audio>. Exposes filter
 * state to the parent grid via the render-prop `children` callback so the
 * grid can hide cards for bands the visitor turned off.
 */
export default function MusicalCollectionBar({ children }) {
  const audioRef = useRef(null);

  // ---- Persistence ------------------------------------------------------
  const restore = () => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch {
      return null;
    }
  };
  const persisted = restore() || {};

  const [activeGenres, setActiveGenres] = useState(
    () => new Set(persisted.activeGenres || [])
  );
  const [disabledBands, setDisabledBands] = useState(
    () => new Set(persisted.disabledBands || [])
  );
  const [shuffle, setShuffle] = useState(persisted.shuffle || false);
  const [expanded, setExpanded] = useState(false); // band-toggle drawer
  const [currentIdx, setCurrentIdx] = useState(0); // index into playlist
  const [playing, setPlaying] = useState(false);
  const [now, setNow] = useState(null); // { bandSlug, trackSlug, title, bandName, accent }
  const [loadError, setLoadError] = useState(false); // sample missing on this pod
  // catalogReady: null = probing, true = audio serves, false = tracks not on this build
  const [catalogReady, setCatalogReady] = useState(null);
  // availableSet: null = manifest not yet fetched, Set = only these slugs have on-disk mp3s.
  // 2026-08-29 Founder plain-ink — the bar was disabling itself because random
  // artist-track slugs in ARTISTS have no on-disk mp3. Manifest hydrates the
  // authoritative set from `${AUDIO_BASE}/manifest.json` so the playlist stops
  // silently trying to play files that don't exist.
  const [availableSet, setAvailableSet] = useState(null);

  useEffect(() => {
    try {
      localStorage.setItem(
        LS_KEY,
        JSON.stringify({
          activeGenres: Array.from(activeGenres),
          disabledBands: Array.from(disabledBands),
          shuffle,
        })
      );
    } catch {}
  }, [activeGenres, disabledBands, shuffle]);

  // ---- Playlist (built live from current filters) ----------------------
  const playlist = useMemo(() => {
    const out = [];
    for (const a of ARTISTS) {
      if (!artistMatchesFilters(a, activeGenres, disabledBands)) continue;
      const tracks = a.tracks || (a.heroTrack ? [a.heroTrack] : []);
      for (const t of tracks) {
        // Filter out tracks whose mp3 isn't on disk yet. When manifest is
        // still loading (availableSet === null), leave everything in so the
        // "N tracks curated" copy renders honestly during the first paint.
        if (availableSet && !availableSet.has(t.slug)) continue;
        out.push({
          bandSlug: a.slug,
          bandName: a.name,
          accent: a.accent,
          trackSlug: t.slug,
          title: t.title,
        });
      }
    }
    if (shuffle) {
      // Fisher-Yates
      for (let i = out.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [out[i], out[j]] = [out[j], out[i]];
      }
    }
    return out;
  }, [activeGenres, disabledBands, shuffle, availableSet]);

  const genres = useMemo(() => allGenres(ARTISTS), []);

  // ---- Catalog readiness probe + track manifest ----------------------
  // First fetches /samples/musical-collection/manifest.json (authoritative
  // list of on-disk mp3 slugs). Falls back to a HEAD probe of a canonical
  // hero track if the manifest is missing. Playlist rendering filters by
  // this set so we stop trying to .play() files that don't exist.
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const res = await fetch(`${AUDIO_BASE}/manifest.json`, { cache: "no-store" });
        if (res.ok) {
          const data = await res.json();
          const slugs = Array.isArray(data.slugs) ? data.slugs : [];
          if (!cancelled) {
            setAvailableSet(new Set(slugs));
            setCatalogReady(slugs.length > 0);
          }
          return;
        }
      } catch {}
      // Fallback: HEAD-probe the first artist's hero track. Older builds
      // shipped without manifest.json; this keeps them from wedging.
      const probeArtist = ARTISTS.find((a) => a.heroTrack?.slug);
      const probeSlug = probeArtist?.heroTrack?.slug;
      if (!probeSlug) {
        if (!cancelled) setCatalogReady(false);
        return;
      }
      try {
        const res = await fetch(`${AUDIO_BASE}/${probeSlug}.mp3`, {
          method: "HEAD",
          cache: "no-store",
        });
        const ct = res.headers.get("content-type") || "";
        if (!cancelled) {
          setCatalogReady(res.ok && ct.startsWith("audio/"));
        }
      } catch {
        if (!cancelled) setCatalogReady(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, []);

  // ---- Playback --------------------------------------------------------
  const playIndex = (idx) => {
    if (catalogReady === false) {
      setLoadError(true);
      return;
    }
    if (playlist.length === 0) return;
    const wrapped = ((idx % playlist.length) + playlist.length) % playlist.length;
    const item = playlist[wrapped];
    setCurrentIdx(wrapped);
    setNow(item);
    setLoadError(false);
    const src = `${AUDIO_BASE}/${item.trackSlug}.mp3`;
    if (audioRef.current) {
      audioRef.current.src = src;
      audioRef.current
        .play()
        .then(() => {
          setPlaying(true);
          setLoadError(false);
        })
        .catch(() => {
          setPlaying(false);
          setLoadError(true);
        });
    }
  };

  const play = () => {
    if (catalogReady === false) {
      setLoadError(true);
      return;
    }
    if (!now && playlist.length) return playIndex(0);
    audioRef.current
      ?.play()
      .then(() => setPlaying(true))
      .catch(() => setLoadError(true));
  };
  const pause = () => {
    audioRef.current?.pause();
    setPlaying(false);
  };
  const next = () => playIndex(currentIdx + 1);
  const prev = () => playIndex(currentIdx - 1);

  const playTrackDirect = (bandSlug, trackSlug) => {
    if (catalogReady === false) {
      setLoadError(true);
      return;
    }
    // Manifest guard — if we know the mp3 isn't on disk, don't set audio.src
    // at all. Otherwise HTMLAudioElement fires MEDIA_ERR_SRC_NOT_SUPPORTED
    // (code 4) and the bar wedges silently on a stale now-playing chip.
    if (availableSet && !availableSet.has(trackSlug)) {
      setLoadError(true);
      setPlaying(false);
      setNow(null);
      return;
    }
    const idx = playlist.findIndex(
      (p) => p.bandSlug === bandSlug && p.trackSlug === trackSlug
    );
    if (idx >= 0) return playIndex(idx);
    // Track isn't in playlist (band filtered out) — enable it and re-search.
    setDisabledBands((s) => {
      const copy = new Set(s);
      copy.delete(bandSlug);
      return copy;
    });
    // Fall back to direct play so the visitor still hears something now.
    setLoadError(false);
    if (audioRef.current) {
      audioRef.current.src = `${AUDIO_BASE}/${trackSlug}.mp3`;
      audioRef.current
        .play()
        .then(() => setPlaying(true))
        .catch(() => {
          setLoadError(true);
          setPlaying(false);
          setNow(null);
        });
    }
  };

  const toggleGenre = (g) => {
    setActiveGenres((s) => {
      const c = new Set(s);
      if (c.has(g)) c.delete(g);
      else c.add(g);
      return c;
    });
  };
  const clearGenres = () => setActiveGenres(new Set());
  const toggleBand = (slug) => {
    setDisabledBands((s) => {
      const c = new Set(s);
      if (c.has(slug)) c.delete(slug);
      else c.add(slug);
      return c;
    });
  };
  const enableAllBands = () => setDisabledBands(new Set());
  const disableAllBands = () =>
    setDisabledBands(new Set(ARTISTS.map((a) => a.slug)));

  const activeBandCount =
    ARTISTS.length -
    ARTISTS.filter((a) => disabledBands.has(a.slug)).length;

  return (
    <>
      {/* GENRE CHIP STRIP (rendered inline where children want it) */}
      {children({
        activeGenres,
        disabledBands,
        genres,
        toggleGenre,
        clearGenres,
        playlist,
        currentBandSlug: now?.bandSlug,
        currentTrackSlug: now?.trackSlug,
        onPlayTrack: playTrackDirect,
        catalogReady,
        availableSet,
      })}

      {/* PERSISTENT BOTTOM BAR */}
      <div
        data-testid="collection-bar"
        className="fixed bottom-0 inset-x-0 z-40 bg-[#050505]/95 backdrop-blur-xl border-t border-white/10"
      >
        <div className="max-w-[1600px] mx-auto px-5 sm:px-8 py-3 flex items-center gap-4">
          {/* Now-playing chip */}
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <span
              className="w-2.5 h-2.5 rounded-full shrink-0"
              style={{
                background: now?.accent || "#3f3f46",
                boxShadow: playing ? `0 0 12px ${now?.accent || "#ff5a00"}` : "none",
              }}
            />
            <div className="min-w-0">
              <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#a1a1aa] truncate">
                {now?.bandName || "The Resilience Studios Musical Collection"}
              </p>
              <p
                data-testid="collection-now-title"
                className="font-display text-sm sm:text-base text-[#ededed] truncate"
              >
                {catalogReady === false
                  ? "Baking the catalog now · refresh for new tracks"
                  : now?.title ||
                    (playlist.length > 0
                      ? `${playlist.length} tracks curated · press play`
                      : "No tracks — enable a band or a genre")}
              </p>
              {catalogReady === false && (
                <p
                  data-testid="collection-catalog-rebaking"
                  className="font-mono text-[9px] uppercase tracking-[0.28em] text-[#ff5a00] mt-1"
                >
                  Fresh tracks land as the bake finishes · refresh in a minute
                </p>
              )}
              {loadError && catalogReady !== false && (
                <p
                  data-testid="collection-load-error"
                  className="font-mono text-[9px] uppercase tracking-[0.28em] text-[#ff5a00] mt-1"
                >
                  Sample queued for re-bake · not on this build
                </p>
              )}
            </div>
          </div>

          {/* Transport */}
          <div className="flex items-center gap-2 sm:gap-3">
            <button
              onClick={prev}
              data-testid="collection-prev"
              aria-label="Previous track"
              className="w-8 h-8 flex items-center justify-center text-[#a1a1aa] hover:text-[#ededed] transition-colors"
            >
              ⏮
            </button>
            <button
              onClick={playing ? pause : play}
              data-testid="collection-play"
              aria-label={playing ? "Pause" : "Play"}
              disabled={catalogReady === false}
              className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors font-bold ${
                catalogReady === false
                  ? "bg-[#3f3f46] text-[#71717a] cursor-not-allowed"
                  : "bg-[#ff5a00] text-black hover:bg-[#ffbb00]"
              }`}
            >
              {playing ? "⏸" : "▶"}
            </button>
            <button
              onClick={next}
              data-testid="collection-next"
              aria-label="Next track"
              className="w-8 h-8 flex items-center justify-center text-[#a1a1aa] hover:text-[#ededed] transition-colors"
            >
              ⏭
            </button>
            <button
              onClick={() => setShuffle((s) => !s)}
              data-testid="collection-shuffle"
              aria-label="Toggle shuffle"
              className={`hidden sm:inline-flex w-8 h-8 items-center justify-center text-xs transition-colors ${
                shuffle
                  ? "text-[#ff5a00]"
                  : "text-[#52525b] hover:text-[#a1a1aa]"
              }`}
            >
              ⇄
            </button>
          </div>

          {/* Bands drawer toggle */}
          <button
            onClick={() => setExpanded((e) => !e)}
            data-testid="collection-bands-toggle"
            className="hidden sm:inline-flex font-mono text-[10px] uppercase tracking-[0.32em] text-[#a1a1aa] hover:text-[#ededed] border border-white/10 hover:border-white/30 px-3 py-2 transition-colors"
          >
            {activeBandCount}/{ARTISTS.length} bands ▾
          </button>
        </div>

        {/* Bands drawer — checkbox grid */}
        <AnimatePresence>
          {expanded && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.22 }}
              data-testid="collection-bands-drawer"
              className="overflow-hidden border-t border-white/[0.05]"
            >
              <div className="max-w-[1600px] mx-auto px-5 sm:px-8 py-5">
                <div className="flex items-center justify-between mb-4">
                  <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#a1a1aa]">
                    Curate the Collection · toggle bands on / off
                  </p>
                  <div className="flex items-center gap-3 font-mono text-[9px] uppercase tracking-[0.32em]">
                    <button
                      onClick={enableAllBands}
                      data-testid="collection-enable-all"
                      className="text-[#a1a1aa] hover:text-[#ededed] transition-colors"
                    >
                      Enable all
                    </button>
                    <span className="text-white/20">·</span>
                    <button
                      onClick={disableAllBands}
                      data-testid="collection-disable-all"
                      className="text-[#a1a1aa] hover:text-[#ededed] transition-colors"
                    >
                      Disable all
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 max-h-[220px] overflow-y-auto">
                  {ARTISTS.map((a) => {
                    const disabled = disabledBands.has(a.slug);
                    return (
                      <button
                        key={a.slug}
                        onClick={() => toggleBand(a.slug)}
                        data-testid={`collection-band-toggle-${a.slug}`}
                        aria-pressed={!disabled}
                        className={`text-left px-3 py-2 border transition-colors ${
                          disabled
                            ? "border-white/[0.05] text-[#52525b] bg-transparent"
                            : "border-white/20 text-[#ededed] bg-[#0b0b0d]"
                        }`}
                        style={
                          disabled
                            ? {}
                            : { borderLeftColor: a.accent, borderLeftWidth: 3 }
                        }
                      >
                        <span className="font-display text-sm truncate block">
                          {a.name}
                        </span>
                        <span className="font-mono text-[9px] uppercase tracking-[0.24em] text-[#52525b] block truncate">
                          {inferGenres(a).slice(0, 2).join(" · ")}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <audio
          ref={audioRef}
          onEnded={next}
          onError={() => {
            setPlaying(false);
            setLoadError(true);
          }}
          data-testid="collection-audio"
          preload="none"
        />
      </div>
    </>
  );
}
